import { useState, useEffect } from 'react';
import axios from 'axios';
import { NavLink, useNavigate } from 'react-router-dom';
import { Home, Music, Server, BarChart2, Shield, Settings, LogOut, Bot, Menu, X, ChevronDown, Check, Gamepad2, Terminal, Globe, Pause, Play, SkipForward, Square, Volume2, Gift, LifeBuoy, ScrollText, Coins, ShieldAlert, Cake, UserPlus, ListMusic, MessageCircleQuestion, Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useGuild } from '@/contexts/GuildContext';
import { useTheme, THEME_LABELS } from '@/contexts/ThemeContext';

const API = process.env.REACT_APP_BACKEND_URL;

function MiniPlayer() {
  const [track, setTrack] = useState(null);
  const [acting, setActing] = useState(null);

  useEffect(() => {
    const load = () =>
      axios.get(`${API}/api/music/now-playing`)
        .then(r => setTrack(r.data?.playing?.[0] || null))
        .catch(() => {});
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, []);

  const control = async (action) => {
    if (!track) return;
    setActing(action);
    try {
      await axios.post(`${API}/api/music/control/${track.guild_id}`, { action });
      setTimeout(() => axios.get(`${API}/api/music/now-playing`).then(r => setTrack(r.data?.playing?.[0] || null)).catch(() => {}), 400);
    } catch {}
    finally { setActing(null); }
  };

  if (!track) return null;

  return (
    <div className="mx-3 mb-2 p-2.5 rounded-xl bg-[#5865F2]/10 border border-[#5865F2]/20">
      <div className="flex items-center gap-2 min-w-0">
        {track.thumbnail ? (
          <img src={track.thumbnail} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
        ) : (
          <div className="w-8 h-8 rounded-lg bg-[#5865F2]/30 flex items-center justify-center flex-shrink-0">
            <Music size={12} className="text-[#5865F2]" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-white text-[11px] font-medium font-[Manrope] truncate leading-tight">{track.title}</p>
          <p className="text-zinc-500 text-[10px] truncate">{track.is_paused ? 'Paused' : 'Playing'}</p>
        </div>
      </div>
      <div className="flex items-center justify-between mt-2">
        <button onClick={() => control(track.is_paused ? 'resume' : 'pause')} disabled={!!acting}
          className="p-1 rounded-md hover:bg-white/10 text-zinc-300 transition-colors disabled:opacity-40">
          {acting === 'pause' || acting === 'resume'
            ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
            : track.is_paused ? <Play size={12} /> : <Pause size={12} />}
        </button>
        <button onClick={() => control('skip')} disabled={!!acting}
          className="p-1 rounded-md hover:bg-white/10 text-zinc-300 transition-colors disabled:opacity-40">
          {acting === 'skip'
            ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
            : <SkipForward size={12} />}
        </button>
        <button onClick={() => control('stop')} disabled={!!acting}
          className="p-1 rounded-md hover:bg-white/10 text-red-400 hover:text-red-300 transition-colors disabled:opacity-40">
          {acting === 'stop'
            ? <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
            : <Square size={12} />}
        </button>
        <div className="flex items-center gap-0.5 ml-auto">
          {[2, 4, 3, 4, 2].map((h, i) => (
            <div key={i} className={`w-0.5 rounded-full ${track.is_paused ? 'bg-zinc-600' : 'bg-[#5865F2]'}`}
              style={{ height: `${h * 2}px`, animation: track.is_paused ? 'none' : `eqBounce${i % 4 + 1} 0.8s ease-in-out infinite` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

const navItems = [
  { path: '/', label: 'Dashboard', icon: Home, testId: 'nav-dashboard', exact: true },
  { path: '/song-requests', label: 'Song Requests', icon: ListMusic, testId: 'nav-song-requests', everyone: true },
  { path: '/music', label: 'Music Stats', icon: Music, testId: 'nav-music' },
  { path: '/server', label: 'Server Stats', icon: Server, testId: 'nav-server' },
  { path: '/usage', label: 'Bot Usage', icon: BarChart2, testId: 'nav-usage' },
  { path: '/game-alerts', label: 'Game Alerts', icon: Gamepad2, testId: 'nav-game-alerts', staffOnly: true },
  { path: '/moderation', label: 'Moderation', icon: Shield, testId: 'nav-moderation', modOnly: true },
  { path: '/tickets', label: 'Tickets', icon: LifeBuoy, testId: 'nav-tickets', modOnly: true },
  { path: '/server-logs', label: 'Server Logs', icon: ScrollText, testId: 'nav-server-logs', modOnly: true },
  { path: '/giveaways', label: 'Giveaways', icon: Gift, testId: 'nav-giveaways' },
  { path: '/economy', label: 'Economy', icon: Coins, testId: 'nav-economy' },
  { path: '/invites', label: 'Invites', icon: UserPlus, testId: 'nav-invites' },
  { path: '/automod', label: 'AutoMod', icon: ShieldAlert, testId: 'nav-automod', staffOnly: true },
  { path: '/birthdays', label: 'Birthdays', icon: Cake, testId: 'nav-birthdays', staffOnly: true },
  { path: '/commands', label: 'Commands', icon: Terminal, testId: 'nav-commands', staffOnly: true },
  { path: '/settings', label: 'Settings', icon: Settings, testId: 'nav-settings', staffOnly: true },
  { path: '/soundboard', label: 'Soundboard', icon: Volume2, testId: 'nav-soundboard' },
  { path: '/bot-log', label: 'Bot Log', icon: Terminal, testId: 'nav-bot-log', ownerOnly: true },
];

function DiscordLogo() {
  return (
    <svg width="24" height="18" viewBox="0 0 71 55" fill="currentColor">
      <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.44077 45.4204 0.52529C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.52529C25.5141 0.44359 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.292408 45.3914C0.297518 45.4562 0.334268 45.5182 0.385858 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6018 45.5576C70.6562 45.5182 70.6901 45.459 70.6952 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978Z"/>
    </svg>
  );
}

function ServerSwitcher() {
  const { guilds, selectedGuild, selectGuild, loading } = useGuild();
  const [open, setOpen] = useState(false);

  if (loading) return null;
  if (guilds.length === 0) {
    return (
      <div className="px-3 pb-3">
        <p className="text-xs text-zinc-600 font-[Manrope] px-3">No shared servers found</p>
      </div>
    );
  }

  return (
    <div className="px-3 pb-3 relative" data-testid="server-switcher">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="server-switcher-btn"
        className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-[var(--bg-hover)] hover:bg-white/8 border border-[var(--border)] transition-colors text-left"
      >
        {selectedGuild?.icon ? (
          <img src={selectedGuild.icon} alt="" className="w-7 h-7 rounded-lg flex-shrink-0" />
        ) : (
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${selectedGuild?.is_global ? 'bg-amber-500/20' : 'bg-[#5865F2]/20'}`}>
            {selectedGuild?.is_global ? <Globe size={14} className="text-amber-400" /> : <Server size={14} className="text-[#5865F2]" />}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-medium font-[Manrope] truncate">{selectedGuild?.name || 'Select a server'}</p>
          {selectedGuild && (
            <p className="text-[11px] text-zinc-500 truncate">
              {selectedGuild.is_global ? 'Owner view' : (selectedGuild.is_staff ? 'Staff' : 'Member')} · {selectedGuild.member_count?.toLocaleString?.() || selectedGuild.member_count} members
            </p>
          )}
        </div>
        <ChevronDown size={15} className={`text-zinc-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              className="absolute left-3 right-3 mt-1.5 z-40 bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-xl shadow-xl overflow-hidden max-h-72 overflow-y-auto"
              data-testid="server-switcher-menu"
            >
              {guilds.map(g => (
                <button
                  key={g.id}
                  onClick={() => { selectGuild(g.id); setOpen(false); }}
                  data-testid={`server-option-${g.id}`}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-[var(--bg-hover)] transition-colors text-left"
                >
                  {g.icon ? (
                    <img src={g.icon} alt="" className="w-7 h-7 rounded-lg flex-shrink-0" />
                  ) : (
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${g.is_global ? 'bg-amber-500/20' : 'bg-[#5865F2]/20'}`}>
                      {g.is_global ? <Globe size={14} className="text-amber-400" /> : <Server size={14} className="text-[#5865F2]" />}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium font-[Manrope] truncate">{g.name}</p>
                    <p className="text-[11px] text-zinc-500 truncate">{g.is_global ? 'Owner view' : (g.is_staff ? 'Staff' : 'Member')}</p>
                  </div>
                  {g.id === selectedGuild?.id && <Check size={15} className="text-[#5865F2] flex-shrink-0" />}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { theme, cycleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [botInfo, setBotInfo] = useState(() => {
    try {
      const cached = localStorage.getItem('botInfo');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const { selectedGuild } = useGuild();

  useEffect(() => {
    axios.get(`${API}/api/bot/status`).then(r => {
      setBotInfo(r.data);
      try { localStorage.setItem('botInfo', JSON.stringify(r.data)); } catch {}
    }).catch(() => {});
  }, []);

  const isOwner = !!(botInfo?.owner_id && user?.user_id && String(user.user_id) === String(botInfo.owner_id));
  const botName = botInfo?.bot_name || 'Bot Dashboard';
  const botAvatar = botInfo?.bot_avatar || null;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getAvatar = () => {
    if (user?.avatar && user?.user_id) {
      return `https://cdn.discordapp.com/avatars/${user.user_id}/${user.avatar}.png`;
    }
    return null;
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-6 py-5 border-b border-[var(--border)]">
        {botAvatar ? (
          <img src={botAvatar} alt="" className="w-9 h-9 rounded-xl flex-shrink-0 object-cover" />
        ) : (
          <div className="w-9 h-9 bg-[#5865F2] rounded-xl flex items-center justify-center flex-shrink-0">
            <DiscordLogo />
          </div>
        )}
        <div>
          <p className="text-white font-semibold font-[Outfit] text-sm leading-tight">{botName}</p>
          <p className="text-zinc-500 text-xs font-[Manrope]">Music & Analytics</p>
        </div>
      </div>

      <ServerSwitcher />

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <p className="text-xs font-semibold text-zinc-600 uppercase tracking-widest px-3 pb-2 font-[Manrope]">Navigation</p>
        {navItems.filter(item => selectedGuild?.limited ? (item.exact || item.everyone) : (!item.staffOnly || (selectedGuild?.is_staff && !selectedGuild?.is_global)) && (!item.modOnly || ((selectedGuild?.is_mod || selectedGuild?.is_staff) && !selectedGuild?.is_global)) && (!item.ownerOnly || isOwner)).map(({ path, label, icon: Icon, testId, exact }) => (
          <NavLink
            key={path}
            to={path}
            end={exact}
            data-testid={testId}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `sidebar-link ${isActive ? 'active' : ''}`
            }
          >
            <Icon size={18} className="flex-shrink-0" />
            <span>{exact && selectedGuild?.limited ? 'Member Hub' : label}</span>
          </NavLink>
        ))}
      </nav>

      <MiniPlayer />

      {/* User + Logout */}
      <div className="px-3 py-4 border-t border-[var(--border)]">
        <div className="flex items-center gap-3 px-3 py-2 rounded-lg mb-2">
          {getAvatar() ? (
            <img src={getAvatar()} alt="avatar" className="w-8 h-8 rounded-full" />
          ) : (
            <div className="w-8 h-8 bg-[#5865F2] rounded-full flex items-center justify-center text-white text-sm font-bold">
              {(user?.global_name || user?.username || '?')[0].toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium font-[Manrope] truncate">
              {user?.global_name || user?.username || 'User'}
            </p>
            <p className="text-zinc-500 text-xs truncate">{selectedGuild?.is_staff ? 'Staff' : 'Member'}</p>
          </div>
        </div>
        {(selectedGuild?.is_staff || isOwner) ? (
          <NavLink
            to="/support"
            data-testid="support-link"
            className={({ isActive }) => `w-full sidebar-link mb-1 ${isActive ? 'active' : 'text-zinc-400 hover:text-white'} transition-colors`}
          >
            <MessageCircleQuestion size={18} />
            <span>Support</span>
          </NavLink>
        ) : botInfo?.support_url ? (
          <a
            href={botInfo.support_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="support-link"
            className="w-full sidebar-link text-zinc-400 hover:text-white transition-colors mb-1"
          >
            <MessageCircleQuestion size={18} />
            <span>Support</span>
          </a>
        ) : (
          <div
            data-testid="support-hint"
            title="Server admins can run /feedback in Discord to message the developer"
            className="w-full sidebar-link text-zinc-500 mb-1 cursor-default"
          >
            <MessageCircleQuestion size={18} />
            <span>Help: use /feedback</span>
          </div>
        )}
        {botInfo?.donate_url && (
          <a
            href={botInfo.donate_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="donate-link"
            className="w-full sidebar-link text-zinc-400 hover:text-pink-400 transition-colors mb-1"
          >
            <Heart size={18} />
            <span>Donate</span>
          </a>
        )}
        <button
          onClick={cycleTheme}
          title={`Theme: ${THEME_LABELS[theme]?.label}`}
          className="w-full sidebar-link text-zinc-400 hover:text-white transition-colors mb-1"
        >
          <span className="text-base leading-none">{THEME_LABELS[theme]?.icon}</span>
          <span>{THEME_LABELS[theme]?.label} Theme</span>
        </button>
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="w-full sidebar-link text-zinc-400 hover:text-red-400 hover:bg-red-500/5 transition-colors"
        >
          <LogOut size={18} />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        data-testid="sidebar"
        className="fixed inset-y-0 left-0 w-60 bg-[var(--bg-surface)] border-r border-[var(--border)] z-40 hidden lg:flex flex-col"
      >
        <SidebarContent />
      </aside>

      {/* Mobile Top Bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[var(--bg-surface)]/90 backdrop-blur border-b border-[var(--border)] flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          {botAvatar ? (
            <img src={botAvatar} alt="" className="w-7 h-7 rounded-lg object-cover" />
          ) : (
            <div className="w-7 h-7 bg-[#5865F2] rounded-lg flex items-center justify-center">
              <DiscordLogo />
            </div>
          )}
          <span className="text-white font-semibold font-[Outfit] text-sm">{botName}</span>
        </div>
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setMobileOpen(true)}
          className="text-zinc-400 hover:text-white p-1"
        >
          <Menu size={22} />
        </button>
      </div>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/60 z-40"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25 }}
              className="lg:hidden fixed inset-y-0 left-0 w-64 bg-[var(--bg-surface)] border-r border-[var(--border)] z-50 flex flex-col"
            >
              <div className="absolute top-4 right-4">
                <button onClick={() => setMobileOpen(false)} className="text-zinc-400 hover:text-white">
                  <X size={20} />
                </button>
              </div>
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
